import type { ReactNode } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseProductBadge from "@/v2-backoffice-client/license/product/LicenseProductBadge"
import Link from "@/v2-backoffice-client/components/link"
import Manage from "@/v2-backoffice-client/components/manage"
import { useAddOnCatalogQuery } from "@/v2-backoffice-client/license/addOns/catalog/license.addOn.catalog.query"
import { useFeatureGroupPlansQuery } from "@/v2-backoffice-client/license/featureGroup/view/license.featureGroup.plans.query"
import { pluralize } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"

export type LicenseFeatureGroupIncludedInTabProps = {
  featureGroupId: number
}

// One titled section of the tab: a count in the title, a list, or a quiet
// line when the list is empty.
function IncludedInSection(props: {
  title: string
  count: number
  isLoading: boolean
  empty: string
  children: ReactNode
}) {
  const { title, count, isLoading, empty, children } = props
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-baseline gap-2 text-sm font-medium">
        {title}
        {!isLoading && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
      </h3>
      {isLoading ? (
        // A list-shaped placeholder only while there is nothing to show:
        // cached data renders through revalidations instead of blanking
        // the section.
        <div className="rounded-md border" aria-busy="true">
          <Manage.Skeleton count={2} />
        </div>
      ) : count === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {empty}
        </p>
      ) : (
        <ul className="flex flex-col divide-y rounded-md border">{children}</ul>
      )}
    </section>
  )
}

// The package panel's "Included in" tab: which licenses and which add-ons
// carry this package — the concrete meaning of "used by N licenses, M add-ons",
// and the list to walk when detaching the package before a delete. Two
// short lists, so two sections on one tab rather than a tab each.
// Membership is edited from each license's or add-on's own panel (its Packages
// tab), where the rest of that mapping is in view. The add-ons list filters
// the catalog read the add-ons board already fetched (each add-on carries
// its packages), so no reverse query exists to drift from it.
function LicenseFeatureGroupIncludedInTab(
  props: LicenseFeatureGroupIncludedInTabProps,
) {
  const { featureGroupId } = props

  const plansQuery = useFeatureGroupPlansQuery({
    variables: { featureGroupId },
  })
  const plans =
    (plansQuery.data ?? plansQuery.previousData)?.getFeatureGroupPlans ?? []
  const isPlansLoading =
    plansQuery.loading && !plansQuery.data && !plansQuery.previousData

  const addOnsQuery = useAddOnCatalogQuery()
  const addOns = (
    (addOnsQuery.data ?? addOnsQuery.previousData)?.getAddOnCatalog ?? []
  ).filter((addOn) =>
    addOn.FeatureGroups.some((group) => group.Id === featureGroupId),
  )
  const isAddOnsLoading =
    addOnsQuery.loading && !addOnsQuery.data && !addOnsQuery.previousData

  return (
    <div className="flex flex-col gap-5">
      <ErrorMessage error={plansQuery.error ?? addOnsQuery.error} />

      <IncludedInSection
        title="Licenses"
        count={plans.length}
        isLoading={isPlansLoading}
        empty="No license grants this package. Licenses pick up packages on their own panel's Packages tab."
      >
        {plans.map((plan) => (
          <li key={plan.PlanId} className="flex items-center gap-2 px-3 py-2">
            {/* Push onto the sheet stack — package → license and back. */}
            <Link className="text-sm" to={`>>?v=plan:${plan.PlanId}`}>
              {plan.PlanName}
            </Link>
            {plan.ProductName && (
              <LicenseProductBadge productName={plan.ProductName} />
            )}
          </li>
        ))}
      </IncludedInSection>

      <IncludedInSection
        title="Add-ons"
        count={addOns.length}
        isLoading={isAddOnsLoading}
        empty="No add-on unlocks this package. Add-ons pick up packages on their own panel's Packages tab."
      >
        {addOns.map((addOn) => (
          <li key={addOn.Id} className="flex items-center gap-2 px-3 py-2">
            {/* Push onto the sheet stack — package → add-on and back. */}
            <Link className="text-sm" to={`>>?v=addOn:${addOn.Id}`}>
              {addOn.Name}
            </Link>
            <Badge size="sm" variant={addOn.CustomerId ? "warning" : "neutral"}>
              {addOn.CustomerId ? "Bespoke" : "Catalog"}
            </Badge>
            {addOn.CustomerName && (
              <span className="truncate text-xs text-muted-foreground">
                {addOn.CustomerName}
              </span>
            )}
          </li>
        ))}
      </IncludedInSection>

      <p className="text-xs text-muted-foreground">
        {pluralize(plans.length, "plan")} and{" "}
        {pluralize(addOns.length, "add-on")} carry this package; detach it from
        each before deleting it.
      </p>
    </div>
  )
}

export default LicenseFeatureGroupIncludedInTab
